import { type ReactElement } from "react";
import "./footer.css";

type FooterNavItem = {
  label: string;
  href: string;
  external?: boolean;
  emphasizeBrand?: boolean;
  showExternalIcon?: boolean;
};

type FooterLinkSection = {
  title: string;
  items: FooterNavItem[];
};

type AirlineLink = {
  name: string;
  href: string;
  src: string;
  alt: string;
  logoClassName?: string;
};

type SocialLink = {
  label: string;
  href: string;
  title: string;
  Icon: ({ className }: { className?: string }) => ReactElement;
};

const footerLinkSections: FooterLinkSection[] = [
  {
    title: "About",
    items: [
      {
        label: "About oneworld",
        href: "https://www.oneworld.com/about-the-oneworld-alliance",
        emphasizeBrand: true,
      },
      {
        label: "Member Airlines",
        href: "https://www.oneworld.com/members",
      },
      {
        label: "Benefits",
        href: "https://www.oneworld.com/travel-benefits",
      },
      {
        label: "App",
        href: "https://apps.apple.com/us/app/fly-oneworld/id1194445509",
        external: true,
        showExternalIcon: true,
      },
      {
        label: "Sustainability",
        href: "https://www.oneworld.com/sustainability",
      },
      {
        label: "News",
        href: "https://www.oneworld.com/media-centre",
      },
      {
        label: "Careers",
        href: "https://www.oneworld.com/careers",
      },
      {
        label: "Privacy Policy",
        href: "https://www.oneworld.com/privacy-policy",
      },
    ],
  },
  {
    title: "Products",
    items: [
      {
        label: "Flights",
        href: "https://www.oneworld.com/flight-search?utm_source=oneworld&utm_medium=website&utm_content=footer",
      },
      {
        label: "Round The World",
        href: "https://www.oneworld.com/world-travel",
      },
      {
        label: "Airport Lounges",
        href: "https://www.oneworld.com/airport-lounges",
      },
      {
        label: "Corporate Travel",
        href: "https://www.oneworld.com/business-travel-program",
      },
      {
        label: "Global Support",
        href: "https://www.oneworld.com/global-support",
      },
    ],
  },
];

const airlineRows: AirlineLink[][] = [
  [
    {
      name: "Alaska Airlines",
      href: "https://www.oneworld.com/members/alaska-airlines",
      src: "https://images.ctfassets.net/m9ph4qvas97u/3BwC7uHE7BUgPqdTLheEdL/b58df432b818812383808b1bb44d19f1/01-AS-web-link-logo.png?fm=webp&q=80",
      alt: "Alaska Airlines logo",
    },
    {
      name: "American Airlines",
      href: "https://www.oneworld.com/members/american-airlines",
      src: "https://images.ctfassets.net/m9ph4qvas97u/6RVJqdfYTnul2AZeNwi0pa/53b6e7c94f08a562f50e2cad11a9ba76/02-AA-web-link-logo.png?fm=webp&q=80",
      alt: "American Airlines logo",
    },
    {
      name: "British Airways",
      href: "https://www.oneworld.com/members/british-airways",
      src: "https://images.ctfassets.net/m9ph4qvas97u/3jwsMbxt8QOqSndSCCRxSL/3ce3ad0441a578d0eddf5281327c72be/03-BA-web-link-logo.png?fm=webp&q=80",
      alt: "British Airways logo",
    },
    {
      name: "Cathay Pacific",
      href: "https://www.oneworld.com/members/cathay-pacific",
      src: "https://images.ctfassets.net/m9ph4qvas97u/6aQGNjaEJ5soSwuDl4cS96/b5d9bf832aae5eff873e0ed7f4b64c3f/04-CX-web-link-logo.png?fm=webp&q=80",
      alt: "Cathay Pacific logo",
    },
    {
      name: "Fiji Airways",
      href: "https://www.oneworld.com/members/fiji-airways",
      src: "https://images.ctfassets.net/m9ph4qvas97u/4hV4sJ924fityvOShK2jGD/a8439695cdaab575d6bd2530468dd97b/Fiji_Footer_Logo.png?fm=webp&q=80",
      alt: "Fiji Airways logo",
    },
    {
      name: "Finnair",
      href: "https://www.oneworld.com/members/finnair",
      src: "https://images.ctfassets.net/m9ph4qvas97u/1dvVCXbx382enu3cbqndBL/2d107fc301fbd57403b49b3ad45dae07/05-AY-web-link-logo.png?fm=webp&q=80",
      alt: "Finnair logo",
    },
  ],
  [
    {
      name: "Hawaiian Airlines",
      href: "https://www.oneworld.com/members/hawaiian-airlines",
      src: "https://images.ctfassets.net/m9ph4qvas97u/2HpO3KFBiilyyg2Ts6UbNW/b5402a09c19f12a777af85926c91275e/Hawaian_Footer_Logo.png?fm=webp&q=80",
      alt: "Hawaiian Airlines logo",
    },
    {
      name: "Iberia",
      href: "https://www.oneworld.com/members/iberia",
      src: "https://images.ctfassets.net/m9ph4qvas97u/24MrFCzTeob2FQsnCOMyf2/3407bd3f3bee2cec7d0015dfe109cba4/06-IB-web-link-logo.png?fm=webp&q=80",
      alt: "Iberia logo",
    },
    {
      name: "Japan Airlines",
      href: "https://www.oneworld.com/members/japan-airlines",
      src: "https://images.ctfassets.net/m9ph4qvas97u/2lzpKs1KLt2kfLh0Fi3ath/535ec1d3bceaf4c5926871a3ea4fcca4/07-JL-web-link-logo-h.png?fm=webp&q=80",
      alt: "Japan Airlines logo",
    },
    {
      name: "Malaysia Airlines",
      href: "https://www.oneworld.com/members/malaysia-airlines",
      src: "https://images.ctfassets.net/m9ph4qvas97u/45Wof7KBFgjyF9QRh0SBjP/3116ce493fd2b9dea4cc702b20a4aa0e/08-MH-web-link-logo-h.png?fm=webp&q=80",
      alt: "Malaysia Airlines logo",
    },
    {
      name: "Oman Air",
      href: "https://www.oneworld.com/members/oman-air",
      src: "https://images.ctfassets.net/m9ph4qvas97u/3xUs7dIpszADnkZUr6wjLv/dbbe5783bc57710cbf57a3e79a65753b/Oman_Air-Logo_web.png?fm=webp&q=80",
      alt: "Oman Air logo",
      logoClassName: "events-footer__airline-logo--oman-air",
    },
  ],
  [
    {
      name: "Qantas",
      href: "https://www.oneworld.com/members/qantas",
      src: "https://images.ctfassets.net/m9ph4qvas97u/1FUzchzPqeiS7DVnBZSg3y/a301c013bef6c0d30038b733c39b9ceb/09-QF-web-link-logo-h.png?fm=webp&q=80",
      alt: "Qantas logo",
    },
    {
      name: "Qatar Airways",
      href: "https://www.oneworld.com/members/qatar-airways",
      src: "https://images.ctfassets.net/m9ph4qvas97u/1GXgnr90AI2CtcArGrBLEJ/b70c410e66e00509b3da6dc2b3cdc633/10-QR-web-link-logo-h.png?fm=webp&q=80",
      alt: "Qatar Airways logo",
    },
    {
      name: "Royal Air Maroc",
      href: "https://www.oneworld.com/members/royal-air-maroc",
      src: "https://images.ctfassets.net/m9ph4qvas97u/1Mh6PYmCeN5XgRGOtSgSAl/e247e8a6ec629a105b65ac0463278b21/11-AT-web-link-logo-h.png?fm=webp&q=80",
      alt: "Royal Air Maroc logo",
    },
    {
      name: "Royal Jordanian",
      href: "https://www.oneworld.com/members/royal-jordanian",
      src: "https://images.ctfassets.net/m9ph4qvas97u/6EGyOg0zV6lgKPhjhUgvg0/a776efe0be3a2db6293d0329742c7af9/12-RJ-web-link-logo-h.png?fm=webp&q=80",
      alt: "Royal Jordanian logo",
    },
    {
      name: "SriLankan Airlines",
      href: "https://www.oneworld.com/members/srilankan-airlines",
      src: "https://images.ctfassets.net/m9ph4qvas97u/6lEpqtIlh82qEmd6jHDyTR/8ae46faf08173b6a79c2a1776a5f439b/13-UL-web-link-logo-h.png?fm=webp&q=80",
      alt: "SriLankan Airlines logo",
    },
  ],
];

function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      fill="currentColor"
      viewBox="0 0 30 30"
    >
      <path d="M29.92 14.88c0 8.17-6.7 14.8-14.96 14.8A14.88 14.88 0 0 1 0 14.88C0 6.7 6.7.08 14.96.08c8.26 0 14.96 6.62 14.96 14.8Z" />
      <path
        fill="#fff"
        d="M18.72 15.46h-2.67v9.67H12v-9.67h-1.92v-3.4H12v-2.2c0-1.58.76-4.04 4.08-4.04l3 .01v3.3h-2.17c-.36 0-.86.18-.86.93v2h3.02l-.35 3.4Z"
      />
    </svg>
  );
}

function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className={className}
      fill="currentColor"
    >
      <path d="M14.829 6.302c-.738-.034-.96-.04-2.829-.04s-2.09.007-2.828.04c-1.899.087-2.783.986-2.87 2.87-.033.738-.041.959-.041 2.828s.008 2.09.041 2.829c.087 1.879.967 2.783 2.87 2.87.737.033.959.041 2.828.041 1.87 0 2.091-.007 2.829-.041 1.899-.086 2.782-.988 2.87-2.87.033-.738.04-.96.04-2.829s-.007-2.09-.04-2.828c-.088-1.883-.973-2.783-2.87-2.87ZM12 15.595a3.595 3.595 0 1 1 0-7.19 3.595 3.595 0 0 1 0 7.19Zm3.737-6.491a.84.84 0 1 1 0-1.68.84.84 0 0 1 0 1.68ZM14.333 12a2.333 2.333 0 1 1-4.666 0 2.333 2.333 0 0 1 4.666 0ZM12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0Zm6.958 14.886c-.115 2.545-1.532 3.955-4.071 4.072-.747.034-.986.042-2.887.042s-2.139-.008-2.886-.042c-2.544-.117-3.955-1.529-4.072-4.072C5.008 14.14 5 13.901 5 12c0-1.901.008-2.139.042-2.886.117-2.544 1.529-3.955 4.072-4.071C9.861 5.008 10.099 5 12 5s2.14.008 2.887.043c2.545.117 3.957 1.532 4.071 4.071.034.747.042.985.042 2.886 0 1.901-.008 2.14-.042 2.886Z" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      fill="currentColor"
      viewBox="0 0 30 30"
    >
      <path d="M29.92 14.88c0 8.17-6.7 14.8-14.96 14.8A14.88 14.88 0 0 1 0 14.88C0 6.7 6.7.08 14.96.08c8.26 0 14.96 6.62 14.96 14.8Z" />
      <path
        fill="#fff"
        d="M12.447 7.2H6.842l6.074 8.027-5.71 6.323h1.743l4.762-5.273 3.99 5.273h5.606L16.76 12.9l5.147-5.7h-1.743l-4.199 4.65-3.519-4.65Zm5.95 12.95L9.657 8.6h2.094l8.74 11.55h-2.094Z"
      />
    </svg>
  );
}

function LinkedInIcon({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      fill="currentColor"
      viewBox="0 0 30 30"
    >
      <path d="M29.92 14.88c0 8.17-6.7 14.8-14.96 14.8A14.88 14.88 0 0 1 0 14.88C0 6.7 6.7.08 14.96.08c8.26 0 14.96 6.62 14.96 14.8Z" />
      <path
        fill="#fff"
        d="M7.43 22.15h3.59V11.53H7.43v10.62ZM9.24 6.4c-1.22 0-2.02.8-2.02 1.84 0 1.02.78 1.84 1.98 1.84h.02c1.25 0 2.02-.82 2.02-1.84-.02-1.05-.77-1.84-2-1.84Zm10.54 4.88a3.56 3.56 0 0 0-3.22 1.75v.04h-.02l.02-.04v-1.5h-3.58c.05 1 0 10.62 0 10.62h3.58v-5.93c0-.32.02-.64.12-.86.26-.64.84-1.3 1.83-1.3 1.3 0 1.81.98 1.81 2.4v5.7h3.58v-6.1c0-3.26-1.76-4.78-4.12-4.78Z"
      />
    </svg>
  );
}

function YouTubeIcon({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      fill="currentColor"
      viewBox="0 0 1000 1000"
    >
      <path d="m581.8 491.5-114.6-53.5c-10-4.6-18.2.6-18.2 11.6v100.8c0 11.1 8.2 16.3 18.2 11.6l114.6-53.5c10-4.7 10-12.3 0-17ZM500 10C229.4 10 10 229.4 10 500s219.4 490 490 490 490-219.4 490-490S770.6 10 500 10Zm0 689.1c-250.8 0-255.2-22.6-255.2-199.1S249.2 300.9 500 300.9 755.2 323.5 755.2 500 750.8 699.1 500 699.1Z" />
    </svg>
  );
}

function TikTokIcon({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      fill="currentColor"
      viewBox="0 0 30 30"
    >
      <path d="M29.92 14.88c0 8.17-6.7 14.8-14.96 14.8A14.88 14.88 0 0 1 0 14.88C0 6.7 6.7.08 14.96.08c8.26 0 14.96 6.62 14.96 14.8Z" />
      <path
        fill="#fff"
        d="M20.658 9.85a4.095 4.095 0 0 1-.354-.207 4.986 4.986 0 0 1-.91-.773 4.292 4.292 0 0 1-1.026-2.116h.004C18.294 6.296 18.326 6 18.33 6h-3.09v11.954c0 .16 0 .319-.007.476l-.003.058c0 .009 0 .018-.002.027v.006a2.625 2.625 0 0 1-1.32 2.084c-.39.221-.831.338-1.28.337a2.616 2.616 0 0 1-2.607-2.625 2.616 2.616 0 0 1 2.607-2.624c.273 0 .544.042.803.127l.004-3.148a5.743 5.743 0 0 0-4.425 1.294A6.065 6.065 0 0 0 7.686 15.6c-.13.225-.623 1.129-.682 2.596-.038.833.212 1.696.332 2.052v.008c.075.21.365.927.839 1.53a6.3 6.3 0 0 0 1.339 1.264v-.008l.007.008c1.497 1.017 3.156.95 3.156.95.287-.011 1.25 0 2.342-.518a5.873 5.873 0 0 0 1.902-1.43 5.94 5.94 0 0 0 1.037-1.721 6.45 6.45 0 0 0 .373-1.97v-6.342c.037.023.537.353.537.353s.72.461 1.842.761c.806.214 1.89.26 1.89.26v-3.07c-.38.042-1.152-.078-1.942-.472Z"
      />
    </svg>
  );
}

function FooterExternalLinkIcon({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      fill="currentColor"
      viewBox="0 0 1000 1000"
    >
      <path d="M786 518v178q0 67-47 114t-114 47H161q-67 0-114-47T0 696V232q0-66 47-113t114-48h393q7 0 12 5t5 13v36q0 8-5 13t-12 5H161q-37 0-63 26t-27 63v464q0 37 27 63t63 27h464q37 0 63-27t26-63V518q0-8 5-13t13-5h36q8 0 13 5t5 13Zm214-482v285q0 15-11 25t-25 11q-14 1-25-11l-98-98-364 364q-5 6-13 6t-12-6l-64-64q-6-5-6-12t6-13l364-364-98-98q-11-11-11-25t11-25q11-11 25-11h285q15 0 25 11t11 25Z" />
    </svg>
  );
}

const socialLinks: SocialLink[] = [
  {
    label: "Facebook",
    href: "https://www.facebook.com/oneworldalliance",
    title: "Opens Oneworld Facebook page",
    Icon: FacebookIcon,
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com/oneworld/",
    title: "Opens Oneworld Instagram page",
    Icon: InstagramIcon,
  },
  {
    label: "X",
    href: "https://twitter.com/traveloneworld",
    title: "Opens Oneworld X page",
    Icon: XIcon,
  },
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/oneworld-alliance/",
    title: "Opens Oneworld LinkedIn page",
    Icon: LinkedInIcon,
  },
  {
    label: "YouTube",
    href: "https://www.youtube.com/channel/UCeafKxBlURQa_8t5btpGN5w",
    title: "Opens Oneworld YouTube page",
    Icon: YouTubeIcon,
  },
  {
    label: "TikTok",
    href: "https://www.tiktok.com/@traveloneworld",
    title: "Opens Oneworld TikTok page",
    Icon: TikTokIcon,
  },
];

const airlineLinks = airlineRows.flat();

function renderFooterLabel(item: FooterNavItem) {
  if (item.emphasizeBrand) {
    return (
      <>
        About <b>one</b>world
      </>
    );
  }

  return item.label;
}

export function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="events-footer">
      <div className="events-footer__content">
        <div className="events-footer__stripe" />

        <section
          className="events-footer__top"
          aria-label="oneworld member airlines"
        >
          <div className="events-footer__airline-panel">
            <div className="events-footer__airline-rows">
              {airlineRows.map((row, rowIndex) => (
                <ul
                  key={`airline-row-${rowIndex + 1}`}
                  className={`events-footer__airline-list events-footer__airline-list--${row.length}-items`}
                >
                  {row.map((airline) => (
                    <li
                      key={airline.name}
                      className="events-footer__airline-item"
                    >
                      <a
                        href={airline.href}
                        className="events-footer__airline-link"
                        target="_blank"
                        rel="noreferrer noopener"
                      >
                        <img
                          src={airline.src}
                          alt={airline.alt}
                          className={`events-footer__airline-logo${airline.logoClassName ? ` ${airline.logoClassName}` : ""}`}
                          decoding="async"
                          loading="lazy"
                          referrerPolicy="no-referrer"
                        />
                        <span className="events-footer__screen-reader-only">
                          (opens in a new tab)
                        </span>
                      </a>
                    </li>
                  ))}
                </ul>
              ))}
            </div>

            <ul className="events-footer__airline-list events-footer__airline-list--desktop">
              {airlineLinks.map((airline) => (
                <li key={airline.name} className="events-footer__airline-item">
                  <a
                    href={airline.href}
                    className="events-footer__airline-link"
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    <img
                      src={airline.src}
                      alt={airline.alt}
                      className={`events-footer__airline-logo${airline.logoClassName ? ` ${airline.logoClassName}` : ""}`}
                      decoding="async"
                      loading="lazy"
                      referrerPolicy="no-referrer"
                    />
                    <span className="events-footer__screen-reader-only">
                      (opens in a new tab)
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <div className="events-footer__medium">
          {footerLinkSections.map((section) => (
            <section
              key={section.title}
              className="events-footer__link-section"
            >
              <h3 className="events-footer__list-title">{section.title}</h3>
              <ul className="events-footer__link-list">
                {section.items.map((item) => (
                  <li key={item.label} className="events-footer__link-item">
                    <a
                      href={item.href}
                      className="events-footer__link"
                      aria-label={
                        item.emphasizeBrand
                          ? `${item.label} (opens in a new tab)`
                          : undefined
                      }
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      <span>{renderFooterLabel(item)}</span>
                      {item.showExternalIcon ? (
                        <FooterExternalLinkIcon className="events-footer__external-icon" />
                      ) : null}
                      <span className="events-footer__screen-reader-only">
                        (opens in a new tab)
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <div className="events-footer__bottom">
          <ul className="events-footer__social-links">
            {socialLinks.map(({ label, href, title, Icon }) => (
              <li key={label} className="events-footer__social-item">
                <a
                  href={href}
                  className="events-footer__social-link"
                  title={title}
                  aria-label={title}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  <Icon className="events-footer__social-icon" />
                  <span className="events-footer__screen-reader-only">
                    (opens in a new tab)
                  </span>
                </a>
              </li>
            ))}
          </ul>
          <div className="events-footer__copy-container">
            <p className="events-footer__copy">
              ©{currentYear} <b>one</b>world Management Company, Inc. All rights
              reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
